<template>
    <FormSection
        title="Patient Information"
        type="his-section"
        :full-height="false"
        :application-id="Number(application)"
    >
        <template #content>
            <section class="row py-3 g-0">
                <div class="col-12 col-lg-6 px-4 col-md-12 g-0">
                    <div class="col-12 py-2">
                        <div class="row">
                            <div class="form-group col-12 col-lg-5 pe-lg-2">
                                <label id="Referral_First_Name_Label" for="referral-first-name">
                                    <span class="badge me-1">M0040</span> First Name
                                    <span class="text-danger">*</span>
                                </label>
                                <input
                                    id="Referral_First_Name"
                                    name="referral-first-name"
                                    :class="`form-control ${
                                        v$.referral.Demographics.FirstName.$error ||
                                        errors.Demographics.includes('FirstName')
                                            ? 'border border-warning'
                                            : ''
                                    }`"
                                    maxlength="50"
                                    type="text"
                                    placeholder="Enter First Name"
                                    v-model="referral.Demographics.FirstName"
                                    @blur="v$.referral.Demographics.FirstName.$touch"
                                    @change="(e: any) => handleAllInput(e, 'FirstName')"
                                />
                            </div>
                            <div class="form-group col-4 col-lg-2 pe-lg-2">
                                <label for="referral-middle-initial"> M.I. </label>
                                <input
                                    name="referral-middle-initial"
                                    class="form-control"
                                    maxlength="1"
                                    type="text"
                                    placeholder="Enter M.I."
                                    v-model="referral.Demographics.MiddleInitial"
                                />
                            </div>
                            <div class="form-group col-12 col-lg-5 pe-lg-2">
                                <label id="Referral_Last_Name_Label" for="referral-last-name">
                                    <span class="badge">M0040</span> Last Name
                                    <span class="text-danger">*</span>
                                </label>
                                <input
                                    id="Referral_Last_Name"
                                    name="referral-last-name"
                                    :class="`form-control ${
                                        v$.referral.Demographics.LastName.$error ||
                                        errors.Demographics.includes('LastName')
                                            ? 'border border-warning'
                                            : ''
                                    }`"
                                    maxlength="50"
                                    placeholder="Enter Last Name"
                                    type="text"
                                    v-model="referral.Demographics.LastName"
                                    @blur="v$.referral.Demographics.LastName.$touch"
                                    @change="(e: any) => handleAllInput(e, 'LastName')"
                                />
                            </div>
                            <div class="form-group col-12 col-lg-12 pe-lg-2">
                                <label id="Referral_Gender_Label" for="referral-gender">
                                    <span class="badge me-1">A0810</span> Sex
                                    <span class="text-danger">*</span>
                                </label>
                                <select
                                    id="Referral_Gender"
                                    :class="`form-select ${
                                        v$.referral.Demographics.Gender.$error || errors.Demographics.includes('Gender')
                                            ? 'border border-warning'
                                            : ''
                                    }`"
                                    name="referral-gender"
                                    v-model="referral.Demographics.Gender"
                                    @blur="v$.referral.Demographics.Gender.$touch"
                                    @change="(e: any) => handleAllInput(e, 'Gender')"
                                >
                                    <option value="">Select Sex</option>
                                    <option
                                        v-for="(gender, index) of referralOptions?.gender"
                                        :key="index"
                                        :value="gender.text"
                                    >
                                        {{ gender.text }}
                                    </option>
                                </select>
                            </div>
                            <div class="form-group col-12 col-lg-4 pe-lg-2">
                                <label for="DOB">
                                    <span class="badge me-1">M0066</span> Date of Birth
                                    <span class="text-danger">*</span>
                                </label>
                                <DatePicker :getDate="onDateChange">
                                    <div class="input-group date datepickerInput">
                                        <input
                                            name="DOB"
                                            type="text"
                                            :class="`form-control ${
                                                v$.referral.Demographics.DOB.$error ||
                                                errors.Demographics.includes('DOB')
                                                    ? 'border border-warning'
                                                    : ''
                                            }`"
                                            placeholder="MM/DD/YYYY"
                                            autocomplete="off"
                                            :value="formatDateInput(referral.Demographics.DOB)"
                                            @change="handleDateInputChange"
                                            @blur="v$.referral.Demographics.DOB.$touch"
                                        />
                                        <div class="input-group-addon input-group-append">
                                            <div class="input-group-text">
                                                <i class="fa fa-calendar"></i>
                                            </div>
                                        </div>
                                    </div>
                                </DatePicker>
                            </div>
                            <div class="form-group col-12 col-lg-8 pe-lg-2">
                                <label for="referral-social-security-number">
                                    <span class="badge me-1">M0064</span> Social Security Number
                                    <span class="text-danger">*</span>
                                </label>
                                <span>
                                    <div
                                        :class="`input-group d-flex w-100 flex-nowrap ${
                                            v$.referral.Demographics.SSN.$error || errors.Demographics.includes('SSN')
                                                ? isDuplicateSSN
                                                    ? 'border border-danger'
                                                    : 'border border-warning'
                                                : ''
                                        }`"
                                    >
                                        <div class="flex-grow-1">
                                            <input
                                                name="referral-social-security-number"
                                                maxlength="9"
                                                minlength="9"
                                                :class="`form-control h-100 no-border-radius`"
                                                type="text"
                                                placeholder="Enter SSN"
                                                v-number-only
                                                v-model="socialSecNumber"
                                                :disabled="referral.Demographics.SSNUnknown"
                                                @blur="v$.referral.Demographics.SSN.$touch"
                                                @change="(e: any) => handleAllInput(e, 'SSN')"
                                            />
                                        </div>
                                        <div class="input-group-prepend flex">
                                            <div class="input-group-text form-check py-1 mb-0 rounded-0">
                                                <input
                                                    type="checkbox"
                                                    id="referral-ssn-na"
                                                    v-model="referral.Demographics.SSNUnknown"
                                                    @change="handleSSNUnknown()"
                                                />

                                                <label class="form-check-label ms-2" for="referral-ssn-na">
                                                    <small>Unknown </small>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </span>
                            </div>
                            <div class="form-group col-12 col-lg-12 pe-lg-2">
                                <label for="referral-marital-status"> Marital Status </label>
                                <select
                                    class="form-select"
                                    name="marital-status"
                                    v-model="referral.Demographics.MaritalStatus"
                                >
                                    <option value="0">Select Marital Status</option>
                                    <option
                                        v-for="(status, index) in referralOptions?.maritalStatus"
                                        :key="index"
                                        :value="status.value"
                                    >
                                        {{ status.text }}
                                    </option>
                                </select>
                            </div>
                            <div class="form-group col-12 col-lg-12 pe-lg-2">
                                <label for="referral-mobile-number">
                                    Mobile Phone
                                    <i
                                        class="fas fa-question-circle"
                                        v-tooltip="{
                                            title: `For emergency preparedness, patient will be contacted at this number. This will also populate to the emergency preparedness report.`,
                                            placement: 'right'
                                        }"
                                    ></i>
                                    <span class="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    :class="`form-control phone-number-input ${
                                        v$.referral.Demographics.PhonePreferred.$error ||
                                        errors.Demographics.includes('PhonePreferred')
                                            ? 'border border-warning'
                                            : ''
                                    }`"
                                    maxlength="10"
                                    placeholder="Enter Number"
                                    @input="setPhoneHome($event)"
                                    :value="referral.Demographics.PhonePreferred"
                                    @blur="v$.referral.Demographics.PhonePreferred.$touch"
                                    v-number-only
                                />
                            </div>
                            <div class="form-group col-12 col-lg-12 pe-lg-2">
                                <label for="referral-alternate-phone"> Alternate Phone </label>
                                <input
                                    type="text"
                                    class="form-control phone-number-input"
                                    maxlength="10"
                                    placeholder="Enter Number"
                                    @input="setPhoneMobile($event)"
                                    :value="referral.Demographics.PhoneAlternate"
                                    v-number-only
                                />
                            </div>
                            <div class="form-group col-12 col-lg-12 pe-lg-2">
                                <label for="referral-email"> Email Address</label>
                                <input
                                    name="referral-email"
                                    class="form-control"
                                    maxlength="50"
                                    type="text"
                                    placeholder="Enter Email Address"
                                    v-model="referral.Demographics.EmailAddress"
                                />
                            </div>
                            <div class="form-group col-12 col-lg-12 pe-lg-2">
                                <label for="assign-to-clinical-manager">
                                    Clinical Manager <span class="text-danger">*</span></label
                                >
                                <select
                                    :class="`form-select  ${
                                        v$.referral.Demographics.ClinicalManagerAssigned.$error ||
                                        errors.Demographics.includes('ClinicalManagerAssigned')
                                            ? 'border border-warning'
                                            : ''
                                    }`"
                                    v-model="referral.Demographics.ClinicalManagerAssigned"
                                    @blur="v$.referral.Demographics.ClinicalManagerAssigned.$touch"
                                    @change="(e: any) => handleAllInput(e, 'ClinicalManagerAssigned')"
                                >
                                    <option :value="null">Select Clinical Manager</option>
                                    <option
                                        v-for="(clinMngr, index) of referralOptions?.clinicalManager"
                                        :key="index"
                                        :value="clinMngr.value"
                                    >
                                        {{ clinMngr.text }}
                                    </option>
                                </select>
                            </div>
                            <div class="form-group col-12 col-lg-12 pe-lg-2">
                                <label for="assign-to-clinical-manager">
                                    Case Manager <span class="text-danger">*</span></label
                                >
                                <select
                                    v-model="referral.Demographics.CaseManagerId"
                                    :class="`form-select  ${
                                        v$.referral.Demographics.CaseManagerId.$error ||
                                        errors.Demographics.includes('CaseManagerId')
                                            ? 'border border-warning'
                                            : ''
                                    }`"
                                    @blur="v$.referral.Demographics.CaseManagerId.$touch"
                                    @change="(e: any) => handleAllInput(e, 'CaseManagerId')"
                                >
                                    <option :value="null">Select Case Manager</option>
                                    <option
                                        v-for="(caseManager, index) of referralOptions?.caseManagerId"
                                        :key="index"
                                        :value="caseManager.value"
                                    >
                                        {{ caseManager.text }}
                                    </option>
                                </select>
                            </div>
                            <div class="form-group col-12 col-lg-12 pe-lg-2">
                                <label for="assign-to-clinical-manager">
                                    Assign To Clinical <span class="text-danger">*</span>
                                </label>
                                <select
                                    :class="`form-select  ${
                                        v$.referral.Demographics.UserId.$error || errors.Demographics.includes('UserId')
                                            ? 'border border-warning'
                                            : ''
                                    }`"
                                    v-model="referral.Demographics.UserId"
                                    @blur="v$.referral.Demographics.UserId.$touch"
                                    @change="(e: any) => handleAllInput(e, 'UserId')"
                                >
                                    <option :value="null">Assign To Clinician</option>
                                    <option
                                        v-for="(user, index) of referralOptions?.userId"
                                        :key="index"
                                        :value="user.value"
                                    >
                                        {{ user.text }}
                                    </option>
                                </select>
                            </div>
                            <div class="form-group col-12 col-lg-12 pe-lg-2">
                                <label for="referral-email"
                                    ><span class="badge me-1">(M0020)</span> Patient ID/MRN
                                    <span class="text-danger">*</span></label
                                >
                                <input
                                    name="referral-email"
                                    :class="`form-control  ${
                                        v$.referral.Demographics.PatientIdNumber.$error ||
                                        errors.Demographics.includes('PatientIdNumber')
                                            ? 'border border-warning'
                                            : ''
                                    }`"
                                    maxlength="50"
                                    type="text"
                                    placeholder="Enter ID/MRN"
                                    v-model="referral.Demographics.PatientIdNumber"
                                    @blur="v$.referral.Demographics.PatientIdNumber.$touch"
                                    @change="(e: any) => handleAllInput(e, 'PatientIdNumber')"
                                />
                            </div>
                            <div class="form-group col-12 col-lg-12 pe-lg-2">
                                <label id="Referral_Agency_Branch_Label" for="agency-location-id">
                                    Branch
                                    <span class="text-danger">*</span>
                                </label>
                                <select
                                    id="Referral_Agency_Branch"
                                    name="agency-location-id"
                                    :class="`form-select  ${
                                        v$.referral.Demographics.AgencyLocationId.$error ||
                                        errors.Demographics.includes('AgencyLocationId')
                                            ? 'border border-warning'
                                            : ''
                                    }`"
                                    v-model="referral.Demographics.AgencyLocationId"
                                    @blur="v$.referral.Demographics.AgencyLocationId.$touch"
                                    @change="(e: any) => handleAllInput(e, 'AgencyLocationId')"
                                >
                                    <option value="">Select Branch</option>
                                    <option v-for="(branch, index) in branches" :key="index" :value="branch.id">
                                        {{ branch.name }}
                                    </option>
                                </select>
                            </div>
                            <div class="form-group col-12 col-lg-12 pe-lg-2">
                                <label for="assign-to-clinical-manager">
                                    Default Service Location <span class="text-danger">*</span>
                                </label>
                                <select
                                    :class="`form-select  ${
                                        v$.referral.Demographics.ServiceLocation.$error ||
                                        errors.Demographics.includes('ServiceLocation')
                                            ? 'border border-warning'
                                            : ''
                                    }`"
                                    v-model="referral.Demographics.ServiceLocation"
                                    @blur="v$.referral.Demographics.ServiceLocation.$touch"
                                    @change="(e: any) => handleAllInput(e, 'ServiceLocation')"
                                >
                                    <option :value="null">--Select Option--</option>
                                    <option
                                        v-for="(location, index) of referralOptions?.serviceLocation"
                                        :key="index"
                                        :value="location.value"
                                    >
                                        {{ location.text }}
                                    </option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-12 col-lg-6 px-4 col-md-12 g-0">
                    <div class="col-12 py-2">
                        <div class="w-100">
                            <p class="mb-2 d-flex align-items-center sub-heading">
                                <b class="me-2"> Primary Address</b>
                            </p>
                        </div>
                        <div class="row">
                            <ReferralAddressForm
                                :address="primaryAddress"
                                address-type="PrimaryAddress"
                                address-name="Primary Address"
                                :county-data="primaryCountyData"
                                :show-code="true"
                                :isRequired="true"
                                :isCountyRequired="true"
                                :zipChanged="getPrimaryCounty"
                            />
                        </div>
                    </div>
                    <div class="col-12 py-2">
                        <div class="w-100">
                            <p class="mb-2 d-flex align-items-center sub-heading">
                                <b class="me-2"> Mailing Address</b>
                            </p>
                            <div class="form-check custom-checkbox form-check-inline mb-2">
                                <input
                                    type="checkbox"
                                    id="sameprimarytomailing"
                                    class="form-check-input au-target"
                                    v-model="referral.Demographics.SameAsPhysicalAddress"
                                    @change="isMailingSameAsPrimaryChanged()"
                                />
                                <label class="form-check-label" for="sameprimarytomailing">
                                    <span class="me-1">Same as Primary Address</span>
                                </label>
                            </div>
                        </div>
                        <div class="row">
                            <ReferralAddressForm
                                :address="referral.Demographics.MailingAddress"
                                :county-data="mailingCountyData"
                                address-type="MailingAddress"
                                :show-code="false"
                                :isRequired="true"
                                :isCountyRequired="false"
                                :zipChanged="handleMailingZipChange"
                                address-name="Mailing Address"
                            />
                        </div>
                    </div>
                    <div class="col-12 py-2">
                        <div class="w-100">
                            <p class="d-flex align-items-center sub-heading">
                                <b class="me-2"> Visit Address</b>
                            </p>
                        </div>
                        <div class="w-100" v-if="!referral.Demographics.IsVisitAddress">
                            <input
                                hidden
                                class="form-check-input"
                                type="radio"
                                v-model="referral.Demographics.IsVisitAddress"
                                name="visitaddrees"
                                id="visit-btn"
                                :value="true"
                            />
                            <label
                                class="form-check-label cursor-pointer btn btn-xs btn-outline-primary p-1"
                                for="visit-btn"
                                >+ ADD VISIT ADDRESS</label
                            >
                        </div>
                        <template v-if="referral.Demographics.IsVisitAddress">
                            <div class="w-90">
                                <h6 class="mb-2 d-flex align-items-center">
                                    <button
                                        class="btn table-btn btn-outline-danger me-1 ms-auto"
                                        @click="() => (referral.Demographics.IsVisitAddress = false)"
                                    >
                                        <i class="fas fa-trash-alt"></i>
                                    </button>
                                </h6>
                            </div>
                            <div class="row">
                                <ReferralAddressForm
                                    :address="referral.Demographics.VisitAddress"
                                    :county-data="visitCountyData"
                                    address-type="VisitAddress"
                                    :show-code="false"
                                    :isCountyRequired="false"
                                    :zipChanged="getVisitCounty"
                                    address-name="Visit Address"
                                />
                            </div>
                        </template>
                    </div>
                    <div class="mt-3 ai-validate-address-btn">
                        <button class="button fr ai-validate-btn" @click="validateAddress()">Validate Addresses</button>
                    </div>
                </div>
            </section>
        </template>
    </FormSection>
</template>

<script lang="ts" src="./PatientInformation.ts"></script>

<style lang="scss">
.no-border-radius {
    border-radius: 0 !important;
}
</style>
